import { POLLING_INTERVALS } from 'core/components/PollingData'
import useIdleUser from 'core/hooks/useIdleUser'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useVMHAContext } from './VMHAContext'
import { VMHAEventPhase, VMHAHostStatus } from './types'
import { useSelector } from 'react-redux'
import { resmgrHostsSelector } from '../../selectors'

interface UseVMHAPollingReturn {
  vmhaStatuses: Map<string, VMHAHostStatus>
  loading: boolean
  reloading: boolean
  refetch: () => Promise<void>
  reload: () => Promise<void>
  ongoingVHMAEvent: VMHAHostStatus | null
  reloadHostVMHAStatus: (hostId: string) => Promise<void>
  reloadVMHATaskDetails: (hostId: string) => Promise<void>
}

/**
 * Application-level hook for polling VMHA events with caching
 * - Uses cached data if last fetch was within 5 minutes
 * - Polls every 30 seconds when the tab is in focus
 * - Stops polling when the tab is not in focus
 * - Provides reload() method to force refresh data
 */
export const useVMHAPolling = (): UseVMHAPollingReturn => {
  const {
    vmhaStatuses,
    loading,
    reloading,
    fetchVMHAData,
    reloadHostVMHAStatus,
    reloadVMHATaskDetails,
  } = useVMHAContext()
  const { isUserActive } = useIdleUser(120000) // 2 minutes idle timeout
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const resmgrHosts = useSelector(resmgrHostsSelector)
  const isResmgrHostsLoaded = Array.isArray(resmgrHosts) && resmgrHosts.length > 0

  /**
   * Force reload data, bypassing cache
   */
  const reload = useCallback(async () => {
    await fetchVMHAData(true)
  }, [fetchVMHAData])

  /**
   * Regular fetch that respects cache
   */
  const refetch = useCallback(async () => {
    await fetchVMHAData(false)
  }, [fetchVMHAData])

  /**
   * Get the first ongoing VMHA event (InProgress state)
   * Returns the VMHAHostStatus object or null if no event is in progress
   */
  const ongoingVHMAEvent = useMemo(() => {
    if (!vmhaStatuses || vmhaStatuses.size === 0) {
      return null
    }

    // Find and return the first host with a VMHA event in InProgress state
    for (const status of vmhaStatuses.values()) {
      if (status.event?.phase === VMHAEventPhase.InProgress) {
        return status
      }
    }

    return null
  }, [vmhaStatuses])

  /**
   * Start polling when component mounts and user is active
   */
  useEffect(() => {
    if (isResmgrHostsLoaded) {
      // Initial fetch (will use cache if available and fresh)
      refetch()

      // Set up polling interval
      if (isUserActive) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }

        intervalRef.current = setInterval(() => {
          refetch()
        }, POLLING_INTERVALS.THIRTY_SECONDS)
      } else {
        // Stop polling when user is inactive
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }
    }

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isUserActive, refetch, isResmgrHostsLoaded])

  return {
    vmhaStatuses,
    loading,
    refetch,
    reload,
    ongoingVHMAEvent,
    reloading,
    reloadVMHATaskDetails,
    reloadHostVMHAStatus,
  }
}
